import { useEffect, useRef, useState } from "react";
import QRCode from "qrcode";
import { useLive, post, del, formatPhone, ago } from "../lib/api";
import type {
  Channel,
  AlertTarget,
  AlertType,
  Broker,
  Ignorado,
} from "../lib/api";
import { Card, Pill, Button, Empty, Abas } from "../lib/ui";
import {
  SlidersHorizontal,
  Smartphone,
  Building2,
  BookOpen,
  Repeat,
  BellRing,
  UserX,
  FlaskConical,
  QrCode,
  Power,
  RotateCcw,
  Trash2,
  Plus,
  type LucideIcon,
} from "lucide-react";
import { Followup } from "./Followup";
import { Base } from "./Base";
import { Imoveis } from "./Imoveis";
import { Simulador } from "./Simulador";
import { Ajustes } from "./Ajustes";

/**
 * Configuração: canais de WhatsApp e quem recebe cada tipo de aviso.
 *
 * Antes isso morava em variáveis de ambiente — cadastrar um número exigia
 * editar o `.env` e reiniciar o processo. Quem opera a imobiliária não faz
 * isso, e nem deveria precisar.
 */
/*
 * Rotulo de cada aba, num mapa.
 *
 * Era uma cadeia de ternarios terminando em "Quem recebe avisos" como padrao,
 * e a aba nova caiu nesse padrao: duas abas com o mesmo nome, uma delas
 * mentindo sobre o que abria. Num mapa, aba sem rotulo aparece vazia -- erro
 * visivel em vez de erro plausivel.
 */
const ROTULO: Record<string, string> = {
  ajustes: "Ajustes",
  canais: "WhatsApp",
  imoveis: "Imóveis",
  base: "O que a IA sabe",
  followup: "Follow-up",
  avisos: "Avisos",
  ignorados: "Ignorados",
  simulador: "Simulador",
};

const ICONE: Record<string, LucideIcon> = {
  ajustes: SlidersHorizontal,
  canais: Smartphone,
  imoveis: Building2,
  base: BookOpen,
  followup: Repeat,
  avisos: BellRing,
  ignorados: UserX,
  simulador: FlaskConical,
};

export function Config() {
  const [aba, setAba] = useState<
    | "ajustes"
    | "canais"
    | "avisos"
    | "ignorados"
    | "simulador"
    | "followup"
    | "base"
    | "imoveis"
  >("ajustes");
  // A aba de numeros mostra quantos estao fora do ar, como o menu lateral.
  const { data: canais } = useLive<Channel[]>("/channels", ["channels"]);
  const caidos = canais?.filter((c) => c.active && c.status !== "connected").length ?? 0;
  return (
    <div className="space-y-6">
      <Abas
        atual={aba}
        onChange={setAba}
        abas={(
          [
            "ajustes",
            "canais",
            "imoveis",
            "base",
            "followup",
            "avisos",
            "ignorados",
            "simulador",
          ] as const
        ).map((a) => ({
          id: a,
          rotulo: ROTULO[a],
          icone: ICONE[a],
          alerta: a === "canais" ? caidos : undefined,
        }))}
      />
      {aba === "ajustes" ? (
        <Ajustes />
      ) : aba === "canais" ? (
        <Canais />
      ) : aba === "imoveis" ? (
        <Imoveis />
      ) : aba === "base" ? (
        <Base />
      ) : aba === "followup" ? (
        <Followup />
      ) : aba === "ignorados" ? (
        <Ignorados />
      ) : aba === "simulador" ? (
        <Simulador />
      ) : (
        <Avisos />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Canais
// ---------------------------------------------------------------------------

const STATUS_LABEL: Record<string, { texto: string; tone: string }> = {
  connected: { texto: "conectado", tone: "ok" },
  qr: { texto: "aguardando leitura do QR", tone: "warm" },
  connecting: { texto: "conectando", tone: "neutral" },
  disconnected: { texto: "desconectado", tone: "neutral" },
  banned: { texto: "bloqueado pelo WhatsApp", tone: "danger" },
};

function Canais() {
  const { data: canais, reload } = useLive<Channel[]>("/channels", ["channels"]);
  const [novo, setNovo] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  async function criar() {
    if (!novo.trim()) return;
    const r = await post<{ error?: string }>("/channels", { label: novo });
    if (r.error) {
      setErro(r.error);
      return;
    }
    setErro(null);
    setNovo("");
    reload();
  }

  return (
    <div className="space-y-4">
      <Card className="p-5">
        <h3 className="text-sm font-semibold mb-1">Adicionar um número</h3>
        <p className="text-xs text-ink-3 mb-3 max-w-prose">
          Dê um apelido — “principal”, “plantão”, “lançamentos”. O número em si
          só é conhecido depois que alguém escaneia o QR, por isso o apelido
          vem primeiro.
        </p>
        <div className="flex gap-2 max-w-md">
          <input
            value={novo}
            onChange={(e) => setNovo(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && void criar()}
            placeholder="apelido do canal"
            className="flex-1 bg-surface-2 border border-rule-2 rounded-lg px-3 py-1.5 text-sm
              text-ink placeholder:text-ink-3 focus:border-accent outline-none"
          />
          <Button variant="primary" icone={Plus} onClick={criar} disabled={!novo.trim()}>
            Adicionar
          </Button>
        </div>
        {erro && <p className="text-xs text-danger mt-2">{erro}</p>}
      </Card>

      {canais?.length === 0 && <Empty>Nenhum canal cadastrado ainda.</Empty>}

      {canais?.map((c) => (
        <CanalCard key={c.id} canal={c} onChange={reload} />
      ))}
    </div>
  );
}

function CanalCard({ canal, onChange }: { canal: Channel; onChange: () => void }) {
  const [erroRemocao, setErroRemocao] = useState<string | null>(null);
  const status = STATUS_LABEL[canal.status] ?? {
    texto: canal.status,
    tone: "neutral",
  };

  return (
    <Card
      stripe={
        canal.status === "connected"
          ? "ok"
          : canal.status === "banned"
            ? "danger"
            : canal.status === "qr"
              ? "warm"
              : undefined
      }
      className={`p-5 ${canal.active ? "" : "opacity-55"}`}
    >
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0 flex gap-3.5">
          <span
            className={`w-10 h-10 rounded-xl grid place-items-center shrink-0 ${
              canal.status === "connected"
                ? "bg-ok-dim text-ok"
                : canal.status === "banned"
                  ? "bg-danger-dim text-danger"
                  : "bg-surface-3 text-ink-3"
            }`}
          >
            <Smartphone size={18} />
          </span>
          <div className="min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="font-semibold">{canal.label}</h3>
            <Pill tone={status.tone as never}>{status.texto}</Pill>
            {!canal.active && <Pill>desativado</Pill>}
          </div>

          <div className="text-sm text-ink-2 mt-1 tabular-nums">
            {canal.phone_e164 ? formatPhone(canal.phone_e164) : "número ainda desconhecido"}
          </div>

          <div className="text-xs text-ink-3 mt-1.5 space-y-0.5">
            {canal.status_detail && <div>{canal.status_detail}</div>}
            <div>
              {canal.conversations} conversa(s)
              {canal.last_seen_at && ` · visto ${ago(canal.last_seen_at)}`}
            </div>
          </div>

          {canal.status === "banned" && (
            <p className="text-xs text-danger mt-2 max-w-prose">
              Este número não volta. Cadastre um canal novo com o chip reserva —
              o histórico deste continua acessível nos leads.
            </p>
          )}
          </div>
        </div>

        <div className="flex flex-wrap gap-1.5 shrink-0">
          {canal.active ? (
            <>
              <Button
                icone={canal.status === "connected" ? RotateCcw : QrCode}
                onClick={async () => {
                  await post(`/channels/${canal.id}/reset`);
                  onChange();
                }}
              >
                {canal.status === "connected" ? "Trocar chip" : "Gerar QR"}
              </Button>
              <Button
                variant="danger"
                icone={Power}
                onClick={async () => {
                  await post(`/channels/${canal.id}/deactivate`);
                  onChange();
                }}
              >
                Desativar
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="primary"
                icone={Power}
                onClick={async () => {
                  await post(`/channels/${canal.id}/activate`);
                  onChange();
                }}
              >
                Reativar
              </Button>
              <Button
                variant="danger"
                icone={Trash2}
                onClick={async () => {
                  if (!confirm(`Remover "${canal.label}" da lista?`)) return;
                  const r = await del<{ error?: string }>(
                    `/channels/${canal.id}`,
                  );
                  // A API recusa quando o canal tem conversa: apagar levaria o
                  // historico junto. O motivo vem de la e e mostrado como esta.
                  if (r.error) setErroRemocao(r.error);
                  else onChange();
                }}
              >
                Remover
              </Button>
            </>
          )}
        </div>
      </div>

      {erroRemocao && (
        <p className="text-xs text-danger mt-2 leading-relaxed">
          {erroRemocao}{" "}
          <button
            onClick={() => setErroRemocao(null)}
            className="underline underline-offset-2"
          >
            ok
          </button>
        </p>
      )}

      {canal.qr && <QrPanel qr={canal.qr} />}
    </Card>
  );
}

/**
 * Renderiza o QR no cliente a partir da string crua.
 *
 * O servidor manda a string, não a imagem: são ~200 bytes contra ~4 kB de PNG,
 * e o QR rotaciona a cada 20 segundos. Em imagem, cada rotação seria um
 * upload novo pelo WebSocket.
 */
function QrPanel({ qr }: { qr: string }) {
  const canvas = useRef<HTMLCanvasElement>(null);

  useEffect(() => {
    if (!canvas.current) return;
    void QRCode.toCanvas(canvas.current, qr, {
      width: 240,
      margin: 2,
      // Fundo claro sempre: leitor de QR em fundo escuro falha em muito
      // celular, e este é o único elemento do painel que não pode ser bonito
      // à custa de funcionar.
      color: { dark: "#0d1413", light: "#ffffff" },
    });
  }, [qr]);

  return (
    <div className="mt-4 pt-4 border-t border-rule flex flex-wrap gap-5 items-center">
      <canvas ref={canvas} className="rounded-lg bg-white p-1 shrink-0" />
      <ol className="text-sm text-ink-2 space-y-1.5 list-decimal list-inside">
        <li>Abra o WhatsApp no celular do número</li>
        <li>
          Toque em <span className="text-ink">Aparelhos conectados</span>
        </li>
        <li>
          Toque em <span className="text-ink">Conectar aparelho</span>
        </li>
        <li>Aponte a câmera pra este código</li>
      </ol>
      <p className="text-xs text-ink-3 w-full">
        O código muda sozinho a cada 20 segundos — não precisa recarregar a
        página.
      </p>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Avisos
// ---------------------------------------------------------------------------

/**
 * Números que o atendimento não enxerga.
 *
 * Não é o mesmo que corretor. Corretor é *atendido*: manda mensagem para o
 * número da empresa e recebe comandos de volta. Quem está aqui desaparece
 * antes de existir lead, conversa, painel ou aviso.
 *
 * Serve para quem toca o sistema por dentro — o dono, o pessoal de TI, o
 * celular de quem testa. Sem isso, um "oi" de teste vira lead no funil, a IA
 * começa a qualificar quem trabalha na empresa, e o relatório do mês conta
 * atendimento que nunca existiu.
 */
function Ignorados() {
  const { data, reload } = useLive<Ignorado[]>("/ignorados", [
    "ignored_numbers",
  ]);
  const [phone, setPhone] = useState("");
  const [label, setLabel] = useState("");
  const [erro, setErro] = useState<string | null>(null);

  async function adicionar() {
    if (!phone.trim()) return;
    try {
      await post("/ignorados", { phone, label });
      setPhone("");
      setLabel("");
      setErro(null);
      reload();
    } catch (e) {
      setErro((e as Error).message);
    }
  }

  return (
    <div className="space-y-4">
      <p className="text-xs text-ink-3 max-w-prose">
        Mensagem destes números é descartada na entrada: não vira lead, não
        abre conversa e não gera aviso. É mais forte que cadastrar como
        corretor — quem está aqui não recebe nem resposta de comando.
      </p>

      <Card className="p-3">
        <div className="flex flex-wrap gap-2 items-center">
          <input
            className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink"
            placeholder="(47) 99999-9999"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
          />
          <input
            className="bg-surface-2 border border-rule-2 rounded-lg px-2 py-1.5 text-sm text-ink flex-1 min-w-40"
            placeholder="de quem é (ex.: Hiran — TI)"
            value={label}
            onChange={(e) => setLabel(e.target.value)}
          />
          <Button variant="primary" onClick={adicionar}>
            Ignorar número
          </Button>
        </div>
        {erro && <div className="text-xs text-danger mt-2">{erro}</div>}
        <p className="text-xs text-ink-3 mt-2">
          Pode digitar como quiser — o número é normalizado ao salvar. Vale na
          hora, sem reiniciar nada.
        </p>
      </Card>

      {data && data.length === 0 ? (
        <Empty>Nenhum número ignorado.</Empty>
      ) : (
        <div className="space-y-2">
          {(data ?? []).map((n) => (
            <Card key={n.id} className="p-3 flex items-center gap-3">
              <span className="text-sm text-ink tabular-nums">
                {formatPhone(n.phone_e164)}
              </span>
              <span className="text-sm text-ink-3 truncate">
                {n.label ?? "sem identificação"}
              </span>
              <Button
                variant="danger"
                className="ml-auto"
                onClick={async () => {
                  await del(`/ignorados/${n.id}`);
                  reload();
                }}
              >
                Remover
              </Button>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}

function Avisos() {
  const { data: alvos, reload } = useLive<AlertTarget[]>("/alerts", [
    "notification_targets",
  ]);
  const { data: tipos } = useLive<AlertType[]>("/alerts/types", []);
  const { data: canais } = useLive<Channel[]>("/channels", ["channels"]);
  const { data: brokers } = useLive<Broker[]>("/brokers", []);
  const [editando, setEditando] = useState<AlertTarget | "novo" | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between gap-3">
        <p className="text-xs text-ink-3 max-w-prose">
          Cada número recebe só o que você marcar. O corretor de plantão quer
          saber de lead esperando; quem cuida da operação precisa saber que um
          chip caiu — e não quer ser acordado por cada lead novo.
        </p>
        <Button variant="primary" onClick={() => setEditando("novo")}>
          Adicionar número
        </Button>
      </div>

      {editando && (
        <FormAviso
          alvo={editando === "novo" ? null : editando}
          tipos={tipos ?? []}
          canais={canais ?? []}
          brokers={brokers ?? []}
          onClose={() => setEditando(null)}
          onSaved={() => {
            setEditando(null);
            reload();
          }}
        />
      )}

      {alvos?.length === 0 && !editando && (
        <Empty>
          Nenhum número configurado — os escalonamentos abrem na fila, mas
          ninguém é avisado no WhatsApp.
        </Empty>
      )}

      {alvos?.map((a) => (
        <Card key={a.id} className={`p-4 ${a.active ? "" : "opacity-55"}`}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="font-medium">{a.label}</span>
                <span className="text-xs tabular-nums text-ink-3">
                  {formatPhone(a.phone_e164)}
                </span>
                {!a.active && <Pill>desativado</Pill>}
              </div>

              <div className="flex flex-wrap gap-1.5 mt-2">
                {a.events.map((ev) => (
                  <Pill key={ev} tone={ev === "canal_caiu" ? "danger" : "accent"}>
                    {tipos?.find((t) => t.evento === ev)?.rotulo ?? ev}
                  </Pill>
                ))}
              </div>

              <div className="text-xs text-ink-3 mt-2 space-x-3">
                {a.quiet_from != null && a.quiet_to != null && (
                  <span>
                    silêncio das {a.quiet_from}h às {a.quiet_to}h
                  </span>
                )}
                {a.channel_label && <span>envia pelo canal “{a.channel_label}”</span>}
              </div>
            </div>

            <div className="flex gap-1.5 shrink-0">
              <Button onClick={() => void post(`/alerts/${a.id}/test`)}>
                Testar
              </Button>
              <Button onClick={() => setEditando(a)}>Editar</Button>
              <Button
                variant="danger"
                onClick={async () => {
                  await del(`/alerts/${a.id}`);
                  reload();
                }}
              >
                Remover
              </Button>
            </div>
          </div>
        </Card>
      ))}
    </div>
  );
}

function FormAviso({
  alvo,
  tipos,
  canais,
  brokers,
  onClose,
  onSaved,
}: {
  alvo: AlertTarget | null;
  tipos: AlertType[];
  canais: Channel[];
  brokers: Broker[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const [label, setLabel] = useState(alvo?.label ?? "");
  const [phone, setPhone] = useState(alvo?.phone_e164 ?? "");
  const [events, setEvents] = useState<string[]>(alvo?.events ?? ["handoff"]);
  const [brokerId, setBrokerId] = useState(alvo?.broker_id ?? "");
  const [channelId, setChannelId] = useState(alvo?.channel_id ?? "");
  const [quietFrom, setQuietFrom] = useState<string>(
    alvo?.quiet_from != null ? String(alvo.quiet_from) : "",
  );
  const [quietTo, setQuietTo] = useState<string>(
    alvo?.quiet_to != null ? String(alvo.quiet_to) : "",
  );
  const [erro, setErro] = useState<string | null>(null);

  async function salvar() {
    const r = await post<{ error?: string }>("/alerts", {
      id: alvo?.id,
      label,
      phone,
      events,
      brokerId: brokerId || undefined,
      channelId: channelId || undefined,
      quietFrom: quietFrom === "" ? null : Number(quietFrom),
      quietTo: quietTo === "" ? null : Number(quietTo),
    });
    if (r.error) {
      setErro(r.error);
      return;
    }
    onSaved();
  }

  const campo =
    "w-full bg-surface-2 border border-rule-2 rounded-lg px-3 py-1.5 text-sm text-ink placeholder:text-ink-3 focus:border-accent outline-none";

  return (
    <Card stripe="accent" className="p-4 space-y-4">
      <div className="grid sm:grid-cols-2 gap-3">
        <label className="space-y-1">
          <span className="text-xs text-ink-3">Nome</span>
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            placeholder="João — plantão"
            className={campo}
          />
        </label>
        <label className="space-y-1">
          <span className="text-xs text-ink-3">WhatsApp</span>
          <input
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            placeholder="(47) 98765-4321"
            className={campo}
          />
        </label>
      </div>

      <div>
        <span className="text-xs text-ink-3">Recebe quais avisos</span>
        <div className="grid sm:grid-cols-2 gap-2 mt-1.5">
          {tipos.map((t) => {
            const marcado = events.includes(t.evento);
            return (
              <button
                key={t.evento}
                onClick={() =>
                  setEvents((prev) =>
                    marcado
                      ? prev.filter((e) => e !== t.evento)
                      : [...prev, t.evento],
                  )
                }
                className={`text-left p-2.5 rounded-lg border transition-colors ${
                  marcado
                    ? "border-accent bg-accent-dim"
                    : "border-rule bg-surface-2 hover:border-rule-2"
                }`}
              >
                <div className="text-sm flex items-center gap-2">
                  <span
                    className={`w-3 h-3 rounded-[4px] border shrink-0 ${
                      marcado ? "bg-accent border-accent" : "border-rule-2"
                    }`}
                  />
                  {t.rotulo}
                </div>
                <div className="text-xs text-ink-3 mt-1 leading-snug">
                  {t.descricao}
                </div>
              </button>
            );
          })}
        </div>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        <label className="space-y-1">
          <span className="text-xs text-ink-3">É um corretor?</span>
          <select
            value={brokerId}
            onChange={(e) => setBrokerId(e.target.value)}
            className={campo}
          >
            <option value="">não vincular</option>
            {brokers.map((b) => (
              <option key={b.id} value={b.id}>
                {b.name}
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs text-ink-3">Enviar pelo canal</span>
          <select
            value={channelId}
            onChange={(e) => setChannelId(e.target.value)}
            className={campo}
          >
            <option value="">qualquer conectado</option>
            {canais
              .filter((c) => c.active)
              .map((c) => (
                <option key={c.id} value={c.id}>
                  {c.label}
                </option>
              ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs text-ink-3">Silêncio a partir de</span>
          <select
            value={quietFrom}
            onChange={(e) => setQuietFrom(e.target.value)}
            className={campo}
          >
            <option value="">sem silêncio</option>
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, "0")}h
              </option>
            ))}
          </select>
        </label>

        <label className="space-y-1">
          <span className="text-xs text-ink-3">até</span>
          <select
            value={quietTo}
            onChange={(e) => setQuietTo(e.target.value)}
            className={campo}
          >
            <option value="">—</option>
            {Array.from({ length: 24 }, (_, h) => (
              <option key={h} value={h}>
                {String(h).padStart(2, "0")}h
              </option>
            ))}
          </select>
        </label>
      </div>

      <p className="text-xs text-ink-3">
        “Canal desconectado” ignora a janela de silêncio: um chip fora do ar às
        3h continua fora às 8h, e cada hora parado é lead perdido.
      </p>

      {erro && <p className="text-sm text-danger">{erro}</p>}

      <div className="flex gap-2">
        <Button variant="primary" onClick={salvar} disabled={!label || !phone}>
          Salvar
        </Button>
        <Button variant="ghost" onClick={onClose}>
          Cancelar
        </Button>
      </div>
    </Card>
  );
}
